import React from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { Container } from '../components/common/Container';
import { Button } from '../components/common/Button';
import { Link } from 'react-router-dom';

const ExtensionWrapper = styled.div`
  padding: ${theme.spacing['4xl']} 0;
  min-height: 80vh;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing['3xl']};
`;

const Title = styled.h1`
  font-size: ${theme.fontSizes['4xl']};
  margin-bottom: ${theme.spacing.lg};
  color: ${theme.colors.text.primary};
`;

const Subtitle = styled.p`
  font-size: ${theme.fontSizes.xl};
  color: ${theme.colors.text.secondary};
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
`;

const StatusBanner = styled.div`
  background: linear-gradient(135deg, ${theme.colors.warning}20, ${theme.colors.warning}10);
  border: 2px solid ${theme.colors.warning};
  border-radius: ${theme.radii.xl};
  padding: ${theme.spacing['2xl']};
  margin-bottom: ${theme.spacing['3xl']};
  text-align: center;
`;

const StatusIcon = styled.div`
  font-size: 3rem;
  margin-bottom: ${theme.spacing.md};
`;

const StatusTitle = styled.h2`
  font-size: ${theme.fontSizes['2xl']};
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.md};
`;

const StatusDescription = styled.p`
  font-size: ${theme.fontSizes.lg};
  color: ${theme.colors.text.secondary};
  line-height: 1.6;
`;

const FeatureSection = styled.div`
  margin: ${theme.spacing['3xl']} 0;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${theme.spacing.xl};
  margin-top: ${theme.spacing['2xl']};
  
  @media (min-width: ${theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const FeatureCard = styled.div`
  background-color: ${theme.colors.background};
  border: 1px solid ${theme.colors.borderLight};
  border-radius: ${theme.radii.lg};
  padding: ${theme.spacing.xl};
  transition: transform ${theme.transitions.base};
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const FeatureIcon = styled.div`
  font-size: 2rem;
  margin-bottom: ${theme.spacing.md};
  color: ${theme.colors.primary};
`;

const FeatureTitle = styled.h3`
  font-size: ${theme.fontSizes.xl};
  margin-bottom: ${theme.spacing.sm};
  color: ${theme.colors.text.primary};
`;

const FeatureDescription = styled.p`
  color: ${theme.colors.text.secondary};
  line-height: 1.6;
`;

const CompatibilitySection = styled.div`
  background-color: ${theme.colors.backgroundSecondary};
  border-radius: ${theme.radii.xl};
  padding: ${theme.spacing['2xl']};
  margin: ${theme.spacing['3xl']} 0;
`;

const CompatibilityGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${theme.spacing.lg};
  margin-top: ${theme.spacing.xl};
`;

const CompatibilityItem = styled.div`
  text-align: center;
  padding: ${theme.spacing.lg};
  background-color: ${theme.colors.background};
  border-radius: ${theme.radii.md};
  border: 1px solid ${theme.colors.borderLight};
`;

const CompatibilityIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: ${theme.spacing.sm};
`;

const CompatibilityName = styled.p`
  font-weight: 500;
  color: ${theme.colors.text.primary};
`;

const InstallSection = styled.div`
  background: linear-gradient(135deg, ${theme.colors.primaryLight}, ${theme.colors.secondary});
  border-radius: ${theme.radii.xl};
  padding: ${theme.spacing['3xl']};
  text-align: center;
  margin-top: ${theme.spacing['3xl']};
`;

const InstallTitle = styled.h2`
  font-size: ${theme.fontSizes['2xl']};
  margin-bottom: ${theme.spacing.lg};
  color: ${theme.colors.text.primary};
`;

const InstallSteps = styled.ol`
  text-align: left;
  max-width: 600px;
  margin: 0 auto ${theme.spacing.xl};
  list-style: none;
  counter-reset: step-counter;
`;

const InstallStep = styled.li`
  padding: ${theme.spacing.md} 0;
  color: ${theme.colors.text.secondary};
  line-height: 1.6;
  counter-increment: step-counter;
  position: relative;
  padding-left: ${theme.spacing['2xl']};
  
  &:before {
    content: counter(step-counter);
    position: absolute;
    left: 0;
    top: ${theme.spacing.md};
    width: 28px;
    height: 28px;
    background-color: ${theme.colors.primary};
    color: white;
    border-radius: ${theme.radii.full};
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: ${theme.fontSizes.sm};
  }
`;

const NotificationSection = styled.div`
  background-color: ${theme.colors.backgroundSecondary};
  border-radius: ${theme.radii.lg};
  padding: ${theme.spacing.xl};
  margin-top: ${theme.spacing['2xl']};
  text-align: center;
`;

const NotificationTitle = styled.h3`
  font-size: ${theme.fontSizes.lg};
  margin-bottom: ${theme.spacing.md};
  color: ${theme.colors.text.primary};
`;

const NotificationForm = styled.form`
  display: flex;
  gap: ${theme.spacing.md};
  max-width: 500px;
  margin: 0 auto;
  
  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column;
  }
`;

const EmailInput = styled.input`
  flex: 1;
  padding: ${theme.spacing.md};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  font-size: ${theme.fontSizes.base};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

export const ExtensionPage: React.FC = () => {
  const handleNotifySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle notification signup
  };

  return (
    <ExtensionWrapper>
      <Container>
        <Header>
          <Title>Browser Extension</Title>
          <Subtitle>
            Enhance your prompts directly in ChatGPT, Claude, and other AI interfaces with our powerful browser extension.
          </Subtitle>
        </Header>

        <StatusBanner>
          <StatusIcon>🚧</StatusIcon>
          <StatusTitle>Coming Soon!</StatusTitle>
          <StatusDescription>
            Our browser extension is currently in development and will be available soon. 
            We're putting the finishing touches on a seamless experience that works perfectly 
            with your favorite AI tools. Sign up below to be notified when it launches!
          </StatusDescription>
        </StatusBanner>

        <FeatureSection>
          <h2 style={{ fontSize: theme.fontSizes['2xl'], marginBottom: theme.spacing.lg }}>
            What to Expect
          </h2>
          <FeatureGrid>
            <FeatureCard>
              <FeatureIcon>⚡</FeatureIcon>
              <FeatureTitle>One-Click Enhancement</FeatureTitle>
              <FeatureDescription>
                Improve your prompts instantly without leaving the ChatGPT or Claude interface. 
                Just click the Promptasaurus button next to your input field.
              </FeatureDescription>
            </FeatureCard>
            <FeatureCard>
              <FeatureIcon>🎯</FeatureIcon>
              <FeatureTitle>Smart Context Detection</FeatureTitle>
              <FeatureDescription>
                Our extension automatically understands the context of your conversation and 
                suggests improvements tailored to your specific use case.
              </FeatureDescription>
            </FeatureCard>
            <FeatureCard>
              <FeatureIcon>🔒</FeatureIcon>
              <FeatureTitle>Privacy-First Design</FeatureTitle>
              <FeatureDescription>
                All processing happens locally in your browser. Your prompts never leave your 
                device unless you explicitly choose to use our cloud features.
              </FeatureDescription>
            </FeatureCard>
            <FeatureCard>
              <FeatureIcon>📚</FeatureIcon>
              <FeatureTitle>Template Library</FeatureTitle>
              <FeatureDescription>
                Access our curated collection of prompt templates right from your browser. 
                Save and organize your own templates for quick reuse.
              </FeatureDescription>
            </FeatureCard>
          </FeatureGrid>
        </FeatureSection>

        <CompatibilitySection>
          <h2 style={{ fontSize: theme.fontSizes['2xl'], marginBottom: theme.spacing.md }}>
            Works Where You Work
          </h2>
          <p style={{ color: theme.colors.text.secondary, marginBottom: theme.spacing.xl }}>
            Our extension will be compatible with all major AI platforms and browsers.
          </p>
          <CompatibilityGrid>
            <CompatibilityItem>
              <CompatibilityIcon>💬</CompatibilityIcon>
              <CompatibilityName>ChatGPT</CompatibilityName>
            </CompatibilityItem>
            <CompatibilityItem>
              <CompatibilityIcon>🤖</CompatibilityIcon>
              <CompatibilityName>Claude</CompatibilityName>
            </CompatibilityItem>
            <CompatibilityItem>
              <CompatibilityIcon>🌐</CompatibilityIcon>
              <CompatibilityName>Chrome</CompatibilityName>
            </CompatibilityItem>
            <CompatibilityItem>
              <CompatibilityIcon>🦊</CompatibilityIcon>
              <CompatibilityName>Firefox</CompatibilityName>
            </CompatibilityItem>
            <CompatibilityItem>
              <CompatibilityIcon>🧭</CompatibilityIcon>
              <CompatibilityName>Safari</CompatibilityName>
            </CompatibilityItem>
            <CompatibilityItem>
              <CompatibilityIcon>📐</CompatibilityIcon>
              <CompatibilityName>Edge</CompatibilityName>
            </CompatibilityItem>
          </CompatibilityGrid>
        </CompatibilitySection>

        <InstallSection>
          <InstallTitle>How to Install (When Available)</InstallTitle>
          <InstallSteps>
            <InstallStep>Visit the Chrome Web Store or your browser's extension marketplace</InstallStep>
            <InstallStep>Search for "Promptasaurus"</InstallStep>
            <InstallStep>Click "Add to Browser"</InstallStep>
            <InstallStep>Pin the extension to your toolbar for easy access</InstallStep>
            <InstallStep>Start improving your prompts instantly!</InstallStep>
          </InstallSteps>
          
          <NotificationSection>
            <NotificationTitle>Get Notified When We Launch</NotificationTitle>
            <NotificationForm onSubmit={handleNotifySubmit}>
              <EmailInput 
                type="email" 
                placeholder="Enter your email" 
                required 
              />
              <Button type="submit">Notify Me</Button>
            </NotificationForm>
          </NotificationSection>
        </InstallSection>
      </Container>
    </ExtensionWrapper>
  );
};